import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// All API calls go through this instance.
// withCredentials sends and receives the httpOnly session cookie set by /api/auth/login.
const client = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Lets the auth layer react to a 401 from any endpoint (e.g. expired session
// while the user is browsing — clear local state and bounce them to /login).
let unauthorizedHandler = null;
export function attachUnauthorizedHandler(fn) {
  unauthorizedHandler = fn;
}
client.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    // Don't bounce on 401s coming from the auth probes themselves — those are
    // expected (the AuthContext uses /me to *check* if a session exists).
    const isAuthProbe = url.includes("/auth/me") || url.includes("/auth/login");
    if (status === 401 && !isAuthProbe && typeof unauthorizedHandler === "function") {
      unauthorizedHandler();
    }
    return Promise.reject(error);
  }
);

const api = {
  // Auth
  login: (username, password) => client.post(`/auth/login`, { username, password }),
  logout: () => client.post(`/auth/logout`),
  me: () => client.get(`/auth/me`),

  // Sync
  getSyncStatus: () => client.get(`/sync/status`),
  triggerSync: () => client.post(`/sync/trigger`),
  syncStock: () => client.post(`/sync/stock`),

  // Dashboard
  getOverview: (period = 30) => client.get(`/dashboard/overview`, { params: { period } }),
  getSales: (period = 90, category = "all") =>
    client.get(`/dashboard/sales`, { params: { period, category } }),
  getInventory: (risk = "all", search = "") =>
    client.get(`/dashboard/inventory`, { params: { risk, search } }),

  // Panel de Ventas — agregados ya calculados desde sales_daily.
  // No confundir con getSales(), que trae los 1.787 productos completos.
  getVentasPanel: (period = 30, base = "con_iva", filtros = {}) =>
    client.get(`/ventas/panel`, {
      params: {
        period, base,
        categoria: filtros.categoria || "all",
        proveedor: filtros.proveedor || "all",
        abc: filtros.abc || "all",
        // `dow` se omite cuando no hay selección: el backend elige el día del
        // último día cerrado. Mandar null lo rompería.
        ...(filtros.dow === null || filtros.dow === undefined ? {} : { dow: filtros.dow }),
      },
    }),
  getVentasDetalle: (params = {}) => client.get(`/ventas/detalle`, { params }),
  getVentasAbcMatriz: () => client.get(`/ventas/abc-matriz`),

  // Vistas guardadas del panel (el PM arma las suyas)
  listVentasVistas: () => client.get(`/ventas/vistas`),
  saveVentasVista: (nombre, payload) =>
    client.put(`/ventas/vistas/${encodeURIComponent(nombre)}`, payload),
  deleteVentasVista: (nombre) =>
    client.delete(`/ventas/vistas/${encodeURIComponent(nombre)}`),
  getVentasPresupuesto: () => client.get(`/ventas/presupuesto-categoria`),

  // Mínimo de stock por SKU fijado a mano (pedido 6 del PM)
  listMinStock: () => client.get(`/manual-min-stock`),
  getMinStock: (sku) => client.get(`/manual-min-stock/${encodeURIComponent(sku)}`),
  setMinStock: (sku, payload) =>
    client.put(`/manual-min-stock/${encodeURIComponent(sku)}`, payload),
  deleteMinStock: (sku) => client.delete(`/manual-min-stock/${encodeURIComponent(sku)}`),
  getMinStockLearningSignal: () => client.get(`/manual-min-stock/senal-aprendizaje`),
  getSuppliers: (sort = "totalSales") =>
    client.get(`/dashboard/suppliers`, { params: { sort } }),
  getProducts: (params = {}) => client.get(`/dashboard/products`, { params }),
  getCompras: (params = {}) => client.get(`/dashboard/compras`, { params }),
  getProveedoresData: (includeInactive = false) =>
    client.get(`/dashboard/proveedores`, { params: { include_inactive_suppliers: includeInactive } }),

  // Supplier Status
  getSupplierStatus: () => client.get(`/suppliers/status`),
  toggleSupplier: (supplier_name, is_active) =>
    client.post(`/suppliers/toggle`, { supplier_name, is_active }),

  // AI
  analyzeAI: (context = "") => client.post(`/ai/analyze`, { context }),
  getAIHistory: () => client.get(`/ai/history`),

  // Purchase Orders (OC)
  // Note: these live under /api/purchase-orders directly (not under /dashboard).
  listPurchaseOrders: (params = {}) =>
    client.get(`/purchase-orders`, { params }),
  getPurchaseOrder: (id) => client.get(`/purchase-orders/${id}`),
  // Historial "No llegó": colas liberadas (informativo, NO recibidas, NO descuentan).
  listTailReleased: (days = 3) =>
    client.get(`/purchase-orders/tail-released`, { params: { days } }),
  createPurchaseOrder: (payload) => client.post(`/purchase-orders`, payload),
  activatePurchaseOrder: (id) => client.post(`/purchase-orders/${id}/activate`),
  closePurchaseOrder: (id) => client.post(`/purchase-orders/${id}/close`),
  cancelPurchaseOrder: (id) => client.post(`/purchase-orders/${id}/cancel`),
  deletePurchaseOrder: (id) => client.delete(`/purchase-orders/${id}`),
  exportPurchaseOrderUrl: (id, format = "xlsx") =>
    `${API_BASE}/purchase-orders/${id}/export?format=${format}`,

  // --- OC por correo al proveedor ("Generar y enviar") --- //
  // Estado del envío (habilitado / modo prueba / buzón de prueba). Sin secretos.
  getOCEmailConfig: () => client.get(`/purchase-orders/email-config`),
  // Correos a los que se enviaría la OC de este proveedor (lista vacía si no se
  // capturaron aún). Lo resuelve el backend con la MISMA función que usa el
  // envío: lo que se muestra es lo que va a recibir la orden.
  getOCSupplierEmail: (supplier) =>
    client.get(`/purchase-orders/supplier-email`, { params: { supplier } }),
  // Envía la OC. `send_id` es la llave de idempotencia de ESTE intento: se repite
  // en los retries de red (para no duplicar el correo) y cambia cuando el usuario
  // decide reintentar (intención nueva).
  sendPurchaseOrder: (id, send_id) =>
    client.post(`/purchase-orders/${id}/send`, { send_id }),
  // Captura/actualiza SOLO los correos de pedidos del proveedor (lista: hay
  // proveedores que reciben la orden en varios buzones). Endpoint fino a
  // propósito: el PUT completo de metadata pisaría contacto/teléfono/condiciones.
  // Reemplaza la lista completa, no agrega.
  // `contact_name` es texto libre para el saludo del correo ("A, B"). Va junto a
  // los correos porque responde a la misma pregunta: a quién le escribo.
  upsertSupplierEmails: (supplier, emails, contact_name) =>
    client.put(`/manual-supplier-metadata/${encodeURIComponent(supplier)}/emails`, {
      emails, contact_name,
    }),
  // Manual costs (overlay editable de costos cuando Bsale no tiene)
  // Edit-in-Talia: primer ladrillo. Talia se vuelve fuente de verdad parcial.
  listManualCosts: () => client.get(`/manual-costs`),
  getManualCost: (sku) => client.get(`/manual-costs/${encodeURIComponent(sku)}`),
  upsertManualCost: (sku, payload) =>
    client.put(`/manual-costs/${encodeURIComponent(sku)}`, payload),
  deleteManualCost: (sku) =>
    client.delete(`/manual-costs/${encodeURIComponent(sku)}`),

  // Edit-in-Talia Fase 2: 7 overrides editables nuevos. Cada GET ('') retorna
  // {items, total}; GET/{key} retorna el doc o 404; PUT/{key} upsert; DELETE
  // soft-archive. Auth-gated igual que manual_costs.
  // Keys: sku para SKU-scoped, supplier_canonical (case-preserving) para supplier-scoped.

  // manual_suppliers — proveedor por SKU
  listManualSuppliers: () => client.get(`/manual-suppliers`),
  getManualSupplier: (sku) =>
    client.get(`/manual-suppliers/${encodeURIComponent(sku)}`),
  upsertManualSupplier: (sku, payload) =>
    client.put(`/manual-suppliers/${encodeURIComponent(sku)}`, payload),
  deleteManualSupplier: (sku) =>
    client.delete(`/manual-suppliers/${encodeURIComponent(sku)}`),

  // manual_supplier_skus — SKU del proveedor por SKU interno
  listManualSupplierSkus: () => client.get(`/manual-supplier-skus`),
  getManualSupplierSku: (sku) =>
    client.get(`/manual-supplier-skus/${encodeURIComponent(sku)}`),
  upsertManualSupplierSku: (sku, payload) =>
    client.put(`/manual-supplier-skus/${encodeURIComponent(sku)}`, payload),
  deleteManualSupplierSku: (sku) =>
    client.delete(`/manual-supplier-skus/${encodeURIComponent(sku)}`),

  // manual_lead_times — lead_time_days por proveedor canónico
  listManualLeadTimes: () => client.get(`/manual-lead-times`),
  getManualLeadTime: (supplier) =>
    client.get(`/manual-lead-times/${encodeURIComponent(supplier)}`),
  upsertManualLeadTime: (supplier, payload) =>
    client.put(`/manual-lead-times/${encodeURIComponent(supplier)}`, payload),
  deleteManualLeadTime: (supplier) =>
    client.delete(`/manual-lead-times/${encodeURIComponent(supplier)}`),

  // manual_dias_recep — días entre recepciones por proveedor canónico
  listManualDiasRecep: () => client.get(`/manual-dias-recep`),
  getManualDiasRecep: (supplier) =>
    client.get(`/manual-dias-recep/${encodeURIComponent(supplier)}`),
  upsertManualDiasRecep: (supplier, payload) =>
    client.put(`/manual-dias-recep/${encodeURIComponent(supplier)}`, payload),
  deleteManualDiasRecep: (supplier) =>
    client.delete(`/manual-dias-recep/${encodeURIComponent(supplier)}`),

  // manual_moq — MOQ en CLP por proveedor canónico
  listManualMoq: () => client.get(`/manual-moq`),
  getManualMoq: (supplier) =>
    client.get(`/manual-moq/${encodeURIComponent(supplier)}`),
  upsertManualMoq: (supplier, payload) =>
    client.put(`/manual-moq/${encodeURIComponent(supplier)}`, payload),
  deleteManualMoq: (supplier) =>
    client.delete(`/manual-moq/${encodeURIComponent(supplier)}`),

  // manual_supplier_metadata — contact_name/email/phone/payment_terms
  listManualSupplierMetadata: () => client.get(`/manual-supplier-metadata`),
  getManualSupplierMetadata: (supplier) =>
    client.get(`/manual-supplier-metadata/${encodeURIComponent(supplier)}`),
  upsertManualSupplierMetadata: (supplier, payload) =>
    client.put(`/manual-supplier-metadata/${encodeURIComponent(supplier)}`, payload),
  deleteManualSupplierMetadata: (supplier) =>
    client.delete(`/manual-supplier-metadata/${encodeURIComponent(supplier)}`),

  // manual_attributes — atributos sparse por SKU (mascota, etapa_vida, etc.)
  listManualAttributes: () => client.get(`/manual-attributes`),
  getManualAttributes: (sku) =>
    client.get(`/manual-attributes/${encodeURIComponent(sku)}`),
  upsertManualAttributes: (sku, payload) =>
    client.put(`/manual-attributes/${encodeURIComponent(sku)}`, payload),
  deleteManualAttributes: (sku) =>
    client.delete(`/manual-attributes/${encodeURIComponent(sku)}`),

  // Set canónico de proveedores (gs_proveedores.nombre ∪ KNOWN_ALIASES ∪ overrides activos)
  // Lo consume el dropdown de Edit-in-Talia para el autocomplete.
  listSupplierCanonical: () => client.get(`/supplier-canonical-list`),

  // Proveedores observados (RUT-backed) en recepciones de los SKUs de `supplier` —
  // destaca alternativas reales en el selector de proveedor destino de la OC (sesión 18).
  getOCSupplierOptions: (supplier) =>
    client.get(`/purchase-orders/supplier-options`, { params: { supplier } }),

  // Streams the file as a blob, then triggers a save in the browser.
  downloadPurchaseOrder: async (id, format = "xlsx") => {
    const res = await client.get(`/purchase-orders/${id}/export`, {
      params: { format },
      responseType: "blob",
    });
    const blob = new Blob([res.data], { type: res.headers["content-type"] });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const cd = res.headers["content-disposition"] || "";
    const match = /filename="?([^"]+)"?/.exec(cd);
    a.download = match ? match[1] : `OC.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};

export default api;
